import React from 'react';
import { Link } from 'react-router-dom';
import { BACKEND_URL } from '../../lib/api';
import { ArticleLayout, ArticleSection } from '../layout/ArticleLayout';
import { categorySlugOf } from '../../lib/howItWorksHierarchy';

// Diagram image for the Architecture / Step-by-Step sections, with a dashed
// placeholder fallback for topics that don't have a diagram yet.
const DiagramImage = ({ src, alt }) =>
  src ? (
    <div className="my-6 rounded-md border border-structure bg-aluminum overflow-hidden">
      <img src={`${BACKEND_URL}${src}`} alt={alt} className="w-full object-contain" />
    </div>
  ) : (
    <div className="my-6 rounded-md border border-dashed border-structure bg-aluminum p-8 text-center text-sm text-muted-foreground">
      Diagram placeholder — diagram to be added.
    </div>
  );

const buildToc = (topic) => {
  const toc = [
    { id: 'what-it-is', label: '1. What It Is' },
    { id: 'where-used', label: "2. Where It's Used" },
    { id: 'concept', label: '3. The Concept' },
    { id: 'architecture', label: '4. Architecture / Components' },
    { id: 'step-by-step', label: '5. Step-by-Step Working' },
    { id: 'failure-signs', label: '6. Common Failure Signs' },
  ];
  if (topic.related_article) {
    toc.push({ id: 'related-article', label: '7. Related Article' });
  }
  return toc;
};

// Renders a How-It-Works topic through the same TOC-left/content-right ArticleLayout
// shell Articles use. Shared between the public topic page (DataDrivenArticlePage's
// analogue, HowItWorksTopicPage) and the admin editor's live preview — same pattern as
// ArticleContentRenderer being shared between DataDrivenArticlePage and
// AdminContentEditorPage.
const HowItWorksContentRenderer = ({ topic, favorite }) => {
  const toc = buildToc(topic);

  // Back link goes to the topic's own domain "More Topics" listing (not straight to the
  // main landing page), matching HowItWorksTopicPage's generic-topic behavior.
  const backTo = topic.category
    ? `/how-it-works/section/${categorySlugOf(topic)}/more`
    : '/how-it-works';
  const backLabel = topic.category ? `Back to ${topic.category}` : 'Back to How Does It Work?';

  return (
    <ArticleLayout
      category={topic.category}
      title={topic.title}
      subtitle={topic.short_description}
      toc={toc}
      favorite={favorite}
      backTo={backTo}
      backLabel={backLabel}
    >
      <ArticleSection id="what-it-is" number={1} title="What It Is">
        <p>{topic.what_it_is}</p>
      </ArticleSection>

      <ArticleSection id="where-used" number={2} title="Where It's Used">
        <p>{topic.where_its_used}</p>
      </ArticleSection>

      <ArticleSection id="concept" number={3} title="The Concept">
        <p>{topic.concept}</p>
      </ArticleSection>

      <ArticleSection id="architecture" number={4} title="Architecture / Components">
        <DiagramImage src={topic.architecture_image} alt={topic.architecture_image_alt} />
        {topic.architecture_components?.length > 0 && (
          <ul className="!pl-0 !list-none space-y-3">
            {topic.architecture_components.map((c, i) => (
              <li key={i} className="!mb-0 rounded-md bg-muted/40 px-4 py-3">
                <span className="font-chivo font-bold text-sm">{c.name}</span>
                <span className="text-sm text-muted-foreground"> — {c.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </ArticleSection>

      <ArticleSection id="step-by-step" number={5} title="Step-by-Step Working">
        <DiagramImage src={topic.step_by_step_image} alt={topic.step_by_step_image_alt} />
        {topic.step_by_step_steps?.length > 0 && (
          <ol className="!pl-0 !list-none space-y-2">
            {topic.step_by_step_steps.map((step, i) => (
              <li key={i} className="!mb-0 flex gap-3 text-sm leading-relaxed">
                <span className="font-mono text-autoxium-blue font-bold shrink-0">{i + 1}.</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        )}
      </ArticleSection>

      <ArticleSection id="failure-signs" number={6} title="Common Failure Signs">
        <ul>
          {topic.failure_signs?.map((sign, i) => (
            <li key={i}>{sign}</li>
          ))}
        </ul>
      </ArticleSection>

      {topic.related_article && (
        <ArticleSection id="related-article" number={7} title="Related Article">
          <Link
            to={`/articles/${topic.related_article.article_id}`}
            className="inline-flex items-center gap-2 text-autoxium-blue font-medium hover:underline"
          >
            {topic.related_article.title}
          </Link>
        </ArticleSection>
      )}
    </ArticleLayout>
  );
};

export default HowItWorksContentRenderer;
